import {defineStore} from 'pinia';
import {getToken, getUser, setToken as storeToken, setUser as storeUser, clearAuth as clearStoredAuth} from '@/services/authStorage';
import {computed, ref} from 'vue';
import {useRouter} from 'vue-router';
import api from '../services/api';
import '../services/httpInterceptor';
// import sessionTimer from '@/services/sessionTimer';

export const useAuthStore = defineStore('auth', () => {
    // State
    const token = ref(getToken());
    const user = ref(getUser());
    const loading = ref(false);
    const error = ref(null);

    // Getters
    const isAuthenticated = computed(() => !!token.value);
    const isAdmin = computed(() => user.value?.roleId === 1);
    const isTeacher = computed(() => user.value?.roleId === 2);
    const isParent = computed(() => user.value?.roleId === 3);
    const userName = computed(() => user.value?.displayName || user.value?.nameCode || user.value?.email || '');

    // Actions
    const router = useRouter();

    function login(credentials) {
        loading.value = true;
        error.value = null;

        // Device-/Viewport-Infos und Username für Login-Tracking als Header erfassen
        let deviceHeaders = {};
        try {
            // Username/Loginkennung aus Credentials extrahieren (ohne Passwort)
            const rawLogin =
                (credentials && (credentials.username ?? credentials.email ?? credentials.login)) ?? null;
            const loginUsername =
                rawLogin != null ? String(rawLogin).trim().slice(0, 120) : null;

            if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
                const ua = navigator.userAgent || '';
                const width = Math.round(
                    window.innerWidth ||
                    (document?.documentElement?.clientWidth ?? 0) ||
                    (screen?.width ?? 0)
                );
                const height = Math.round(
                    window.innerHeight ||
                    (document?.documentElement?.clientHeight ?? 0) ||
                    (screen?.height ?? 0)
                );

                let orientation = null;
                const oType = (window.screen && window.screen.orientation && window.screen.orientation.type)
                    ? window.screen.orientation.type
                    : null;
                if (oType) {
                    orientation = /portrait/i.test(oType)
                        ? 'portrait'
                        : (/landscape/i.test(oType) ? 'landscape' : null);
                } else if (width && height) {
                    orientation = height >= width ? 'portrait' : 'landscape';
                }

                const dpr = (typeof window.devicePixelRatio === 'number') ? window.devicePixelRatio : null;

                let deviceName = null;
                if (/iPad/i.test(ua)) deviceName = 'iPad';
                else if (/iPhone/i.test(ua)) deviceName = 'iPhone';
                else if (/Android/i.test(ua) && /Mobile/i.test(ua)) deviceName = 'Android Phone';
                else if (/Android/i.test(ua)) deviceName = 'Android Tablet';
                else if (/Macintosh/i.test(ua)) deviceName = 'Mac';
                else if (/Windows NT/i.test(ua)) deviceName = 'Windows PC';
                else if (/Linux/i.test(ua)) deviceName = 'Linux PC';

                deviceHeaders = {
                    'X-Login-Username': loginUsername || undefined,
                    'X-Device-Name': deviceName || undefined,
                    'X-Device-Width': width || undefined,
                    'X-Device-Height': height || undefined,
                    'X-Device-Orientation': orientation || undefined,
                    'X-Device-Pixel-Ratio': dpr != null ? dpr : undefined
                };
            } else {
                // Falls kein window/navigator verfügbar ist, zumindest Username senden
                if (loginUsername) {
                    deviceHeaders['X-Login-Username'] = loginUsername;
                }
            }
        } catch (_) {
            // Falls Ermittlung fehlschlägt, ohne Zusatz-Header weiterfahren
        }

        return api.post('/login', credentials, {headers: deviceHeaders})
            .then(response => {
                if (!response) {
                    throw new Error('Keine Response erhalten');
                }

                const data = response.data.data || response.data;

                if (!data || typeof data !== 'object') {
                    throw new Error('Ungültige Server-Response');
                }

                if (data.success === true) {
                    setToken(data.token);
                    setUser(data.user);

                    // Permissions asynchron aus /auth/check anreichern (nicht blockierend)
                    try {
                        enrichUserPermissions();
                    } catch (_) {
                        // ignore
                    }

                    // Nach erfolgreichem Login weiterleiten
                    const redirectTarget = data.redirect || '/';
                    // Fallback: falls Server "/dashboard" liefert, mappen wir auf die Dashboard-Route
                    if (redirectTarget === '/dashboard') {
                        window.location.assign('/');
                    } else {
                        window.location.assign(redirectTarget);
                    }

                    return data;
                } else {
                    const errorMessage = data.message || 'Login fehlgeschlagen';
                    error.value = errorMessage;
                    throw new Error(errorMessage);
                }
            })
            .catch(err => {
                if (err.response) {
                    const serverError = err.response.data;
                    if (serverError && serverError.message) {
                        error.value = serverError.message;
                    } else if (serverError?.data?.message) {
                        error.value = serverError.data.message;
                    } else {
                        error.value = `Server Error: ${err.response.status}`;
                    }
                } else if (err.request) {
                    error.value = 'Netzwerkfehler - Server nicht erreichbar';
                } else {
                    error.value = err.message || 'Unbekannter Fehler beim Login';
                }

                throw err;
            })
            .finally(() => {
                loading.value = false;
            });
    }

    function logout() {
        if (token.value) {
            api.post('/logout').catch(() => {
            });
        }

        clearAuth();
        router.push('/login');
    }

    function setToken(newToken) {
        if (!newToken || newToken.trim() === '') {
            return;
        }

        token.value = newToken;
        storeToken(newToken);
        api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

        // Echten TTL-Wert (settings.php → jwt.expiration) aus dem frisch erhaltenen Token setzen
        // try { sessionTimer.initFromToken(newToken); } catch { /* noop */ }
    }

    function checkAuth() {
        const storedToken = getToken();
        if (storedToken) {
            token.value = storedToken;
            api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
            // TTL aus Token rekonstruieren (exp-iat)
            // try { sessionTimer.initFromToken(storedToken); } catch { /* noop */ }

            // User-Objekt (inkl. permissions) aus /auth/check nachladen
            try {
                enrichUserPermissions();
            } catch (_) {
                // ignore
            }
        } else {
            clearAuth();
        }
    }

    function setUser(newUser) {
        user.value = newUser;
        storeUser(newUser);
    }

    // Lädt aktive Permission-Keys via /auth/check und merged sie in den User
    async function enrichUserPermissions() {
        try {
            const res = await api.get('/auth/check');
            const u = res?.data?.user ?? res?.data ?? null;
            if (!u || typeof u !== 'object') return;
            const keys = Array.isArray(u.permissions) ? u.permissions : [];
            if (!user.value) {
                setUser({...u, permissions: keys});
                return;
            }
            const merged = {...user.value, ...u, permissions: keys};
            setUser(merged);
        } catch (_) {
            // still: nicht blockieren
        }
    }

    function updateUser(updatedUserData) {
        if (user.value) {
            user.value = {...user.value, ...updatedUserData};
            storeUser(user.value);
        }
    }

    async function forgotPassword(emailAddress) {
        if (!emailAddress || typeof emailAddress !== 'string') {
            return Promise.reject(new Error('E-Mail-Adresse ist erforderlich'));
        }

        try {
            const res = await api.post('/forgot-password', {email: emailAddress});
            const data = res?.data?.data ?? res?.data ?? {};
            if (data && data.success === false) {
                throw new Error(data.message || 'Fehler beim Senden der E-Mail');
            }
            return data;
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                err?.response?.data?.data?.message ||
                err?.message ||
                'Fehler beim Senden der E-Mail';
            throw new Error(message);
        }
    }

    async function resetPassword(tokenValue, password, confirmPassword) {
        if (!tokenValue) {
            return Promise.reject(new Error('Token fehlt'));
        }
        if (!password || !confirmPassword) {
            return Promise.reject(new Error('Passwort und Bestätigung sind erforderlich'));
        }

        loading.value = true;
        error.value = null;

        try {
            const res = await api.post('/reset-password', {
                token: tokenValue,
                password,
                confirmPassword
            });
            const data = res?.data?.data ?? res?.data ?? {};

            if (data && data.success === false) {
                const msg = data.message || 'Passwort konnte nicht zurückgesetzt werden';
                error.value = msg;
                throw new Error(msg);
            }

            return data;
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                err?.response?.data?.data?.message ||
                err?.message ||
                'Passwort konnte nicht zurückgesetzt werden';
            error.value = message;
            throw new Error(message);
        } finally {
            loading.value = false;
        }
    }

    function clearAuth() {
        token.value = null;
        user.value = null;
        clearStoredAuth();
        delete api.defaults.headers.common['Authorization'];
    }

    // Beim Store-Initialisierung checkAuth ausführen
    checkAuth();

    return {
        // State
        token,
        user,
        loading,
        error,

        // Getters
        isAuthenticated,
        isAdmin,
        isTeacher,
        isParent,
        userName,

        // Actions
        login,
        logout,
        forgotPassword,
        resetPassword,
        checkAuth,
        setToken,
        setUser,
        updateUser,
        clearAuth
    };
});

export const useAuth = () => useAuthStore();

// Benannte Export-Funktion für direkten Import in Views (z. B. Login.vue)
export const login = (credentials) => useAuthStore().login(credentials);

// Zusätzlich: Default-Export, damit import login from '@/stores/auth' funktioniert
export {login as default};